/**
 * Shared IndexedDB connection + transaction layer.
 *
 * All four builder IDB users (pb-state, pb-assets, pb-revisions) go through
 * createIdbHandle() so they share one hardened lifecycle:
 *   - the open promise is cached; a REJECTED open clears the cache so the next
 *     call re-attempts (no permanently-poisoned handle);
 *   - on open success the connection self-heals: db.onclose (browser force-close
 *     after sleep/eviction) and db.onversionchange (another tab upgrading/deleting)
 *     both reset() the cache so the next op reopens instead of throwing forever;
 *   - the open request handles onblocked with a bounded reject rather than hanging;
 *   - promisifyTx() rejects on BOTH oncomplete-failure paths (onerror AND onabort),
 *     which kills the permanent-hang class where an aborted tx never settled;
 *   - withStore() resolves only AFTER the transaction commits (durability, not
 *     request.onsuccess) and retries the whole operation exactly once on a
 *     connection-death error after reset().
 *
 * The idbFactory option is an injection seam (mirrors tab-guard's `win` injection)
 * so tests can drive a scriptable fake IndexedDB. When omitted it resolves
 * globalThis.indexedDB lazily at open time (so a test can set the global first).
 */

const BLOCKED_TIMEOUT_MS = 3000;

function abortError() {
  const err = new Error('IndexedDB transaction aborted');
  err.name = 'AbortError';
  return err;
}

/**
 * True for the DOMException names browsers raise when a cached connection has
 * been force-closed (post-sleep/eviction) — the errors a single retry after
 * reopening can recover from. Quota/Data/Constraint/app-level aborts are NOT
 * connection deaths: the connection is fine, so retrying would double-write.
 * @param {unknown} err
 * @returns {boolean}
 */
export function isConnectionDeadError(err) {
  const name = err && err.name;
  return name === 'InvalidStateError' || name === 'UnknownError';
}

/**
 * Wrap an IDBRequest as a promise that resolves with request.result.
 * Safe to await inside a live transaction (the pending request keeps the tx
 * from auto-committing).
 * @param {IDBRequest} request
 * @returns {Promise<any>}
 */
export function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

/**
 * Wrap an IDBTransaction as a promise that settles on commit. Rejects on BOTH
 * onerror and onabort — an aborted transaction (quota, connection death, or an
 * explicit abort) must reject, never hang.
 * @param {IDBTransaction} tx
 * @returns {Promise<void>}
 */
export function promisifyTx(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction error'));
    tx.onabort = () => reject(tx.error || abortError());
  });
}

function openDb({ name, version, upgrade, idbFactory, blockedTimeoutMs }) {
  const factory =
    idbFactory || (typeof globalThis !== 'undefined' ? globalThis.indexedDB : undefined);
  const blockedMs = Number.isFinite(blockedTimeoutMs) ? blockedTimeoutMs : BLOCKED_TIMEOUT_MS;
  if (!factory) return Promise.reject(new Error('IndexedDB is not available'));
  return new Promise((resolve, reject) => {
    let settled = false;
    let blockedTimer = null;
    const request = factory.open(name, version);

    request.onupgradeneeded = (event) => {
      if (typeof upgrade === 'function') {
        upgrade(request.result, event?.oldVersion ?? 0, event?.newVersion ?? version, request);
      }
    };
    request.onsuccess = () => {
      if (blockedTimer) {
        clearTimeout(blockedTimer);
        blockedTimer = null;
      }
      if (settled) {
        // We already rejected on a blocked-timeout; don't leak the connection.
        try {
          request.result.close();
        } catch {
          /* ignore */
        }
        return;
      }
      settled = true;
      resolve(request.result);
    };
    request.onerror = () => {
      if (blockedTimer) {
        clearTimeout(blockedTimer);
        blockedTimer = null;
      }
      if (settled) return;
      settled = true;
      reject(request.error || new Error('IndexedDB open failed'));
    };
    // Another live connection is blocking an upgrade/delete. Wait a bounded time
    // for it to close, then reject rather than hang forever.
    request.onblocked = () => {
      if (blockedTimer || settled) return;
      blockedTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(`IndexedDB open blocked: "${name}" is held by another connection`));
      }, blockedMs);
    };
  });
}

/**
 * Create a cached, self-healing IndexedDB connection handle.
 * @param {{name:string, version:number, upgrade?:Function, idbFactory?:IDBFactory}} opts
 * @returns {{getDB:()=>Promise<IDBDatabase>, reset:()=>void, close:()=>void}}
 */
export function createIdbHandle({ name, version, upgrade, idbFactory, blockedTimeoutMs } = {}) {
  let dbPromise = null;

  function reset() {
    dbPromise = null;
  }

  function getDB() {
    if (dbPromise) return dbPromise;
    dbPromise = openDb({ name, version, upgrade, idbFactory, blockedTimeoutMs })
      .then((db) => {
        // Self-heal: a force-closed or version-changed connection resets the
        // cache so the next op reopens instead of throwing on a dead handle.
        db.onclose = () => {
          reset();
        };
        db.onversionchange = () => {
          try {
            db.close();
          } catch {
            /* ignore */
          }
          reset();
        };
        return db;
      })
      .catch((err) => {
        // A rejected open must not poison the handle forever.
        dbPromise = null;
        throw err;
      });
    return dbPromise;
  }

  function close() {
    if (!dbPromise) return;
    const pending = dbPromise;
    dbPromise = null;
    pending.then(
      (db) => {
        try {
          db.close();
        } catch {
          /* ignore */
        }
      },
      () => {}
    );
  }

  return { getDB, reset, close };
}

/**
 * Run a transaction against one object store and resolve AFTER it commits.
 *
 * Contract: `fn(objectStore, tx)` must be synchronous with respect to the
 * transaction — it may await IDBRequest promises (promisifyRequest) but must
 * NOT await non-IDB async work inside the live tx. IndexedDB auto-commits when
 * the microtask queue drains with no pending request, so a non-IDB await would
 * silently abort the transaction.
 *
 * On a connection-death error (InvalidStateError/UnknownError — whether thrown
 * at db.transaction() or surfaced via tx abort) the whole operation is retried
 * exactly once after reset(). Quota/Data/Constraint/AbortError propagate
 * immediately with no reset (the connection is fine; retrying would double-write).
 *
 * @param {{getDB:Function, reset:Function}} handle
 * @param {string} storeName
 * @param {IDBTransactionMode} mode
 * @param {(store:IDBObjectStore, tx:IDBTransaction)=>any} fn
 * @returns {Promise<any>} whatever fn returned/resolved to
 */
export async function withStore(handle, storeName, mode, fn) {
  let didReset = false;
  for (;;) {
    let db;
    try {
      db = await handle.getDB();
    } catch (err) {
      if (!didReset && isConnectionDeadError(err)) {
        didReset = true;
        handle.reset();
        continue;
      }
      throw err;
    }

    let tx;
    let objectStore;
    try {
      tx = db.transaction(storeName, mode);
      objectStore = tx.objectStore(storeName);
    } catch (err) {
      // db.transaction() throws InvalidStateError when the connection is dead.
      if (!didReset && isConnectionDeadError(err)) {
        didReset = true;
        handle.reset();
        continue;
      }
      throw err;
    }

    const txDone = promisifyTx(tx);
    let result;
    try {
      result = await fn(objectStore, tx);
      await txDone;
    } catch (err) {
      // If fn threw before commit, tear down the still-live tx and swallow the
      // abort it triggers so it can't surface as an unhandled rejection.
      try {
        tx.abort();
      } catch {
        /* already settled */
      }
      txDone.catch(() => {});
      if (!didReset && isConnectionDeadError(err)) {
        didReset = true;
        handle.reset();
        continue;
      }
      throw err;
    }
    return result;
  }
}
